// utils/excelReport.ts
/**
 * The dashboard's Excel export, built as a report a client can read without us. After the UK
 * Analysis Function's "Releasing statistics in spreadsheets"
 * (https://analysisfunction.civilservice.gov.uk/policy-store/releasing-statistics-in-spreadsheets/):
 * a cover sheet with the context and a table of contents; on every sheet a title in A1 and one
 * table marked up as an Excel table; plain-English headers in the dashboard's own words with units
 * in brackets; real dates and numbers; a sans serif font; no merged cells, no blank rows inside a
 * table, no freeze panes. The brand appears as the name in text and the Blue header row (the guide
 * advises against logo images). The one other use of colour is the heatmaps' native colour scale.
 *
 * Pure: takes the figures and the context, returns the workbook. The export button only fetches and
 * downloads (utils/ExportAllDataToExcel.tsx).
 */
import type ExcelJS from 'exceljs';
import { colors } from '@/styles/base/tokens';
import { estimate } from '@/utils/copyTable';
import { catchAllLast, regionName } from '@/utils/regionCatchAll';
import { responseTimingBins } from '@/utils/responseTiming';

type Kind = 'text' | 'int' | 'rate' | 'share' | 'money' | 'date' | 'estimate' | 'decimal';

interface Column {
    label: string;
    kind: Kind;
    width?: number;
}

/** One sheet: its tab name, its A1 title, a sentence of context, and one table */
interface SheetSpec {
    name: string;
    title: string;
    about: string;
    columns: Column[];
    rows: Array<Array<string | number | Date | null>>;
    /** Colour the numeric cells with Excel's own scale (the heatmaps) */
    colourScale?: boolean;
    /** Where one column mixes units (the Summary's Value column): the kind of each row's value */
    rowKinds?: Kind[];
}

export interface ReportFilter {
    label: string;
    values: string[];
}

export interface ReportContext {
    /** The brand mark for the cover, as a PNG data URL; the name is also written in text beside it */
    logo?: { dataUrl: string; width: number; height: number };
    client: string;
    startDate: Date | null;
    endDate: Date | null;
    filters: ReportFilter[];
    exportedAt: Date;
}

/** The figures as GraphProvider holds them; every field optional, since any widget may be empty */
type Any = any;
export interface ReportData {
    impressionSummary: Any;
    impressionsBeforeVisit: Any;
    responsesByRegion: Any;
    responsesBySite: Any;
    responsesOverTime: Any;
    visitResponseTiming: Any;
    creativePerformanceCost: Any;
    channelPerformanceCost: Any;
    bookingPerformanceCost: Any;
    impressionHeatmap: Any;
    uniqueReach: Any;
    reachBreakdown: Any;
}

const FONT = 'Aptos'; // The brand's Office font (Brand Playbook); Excel substitutes where it is absent
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * A date range end as it may arrive: a Date, or a "YYYY-MM-DD" string restored from storage, read
 * as that local day (a bare ISO date parses as UTC midnight, the day before in the Americas)
 */
export const asLocalDate = (v: unknown): Date | null => {
    if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
    if (typeof v !== 'string') return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
};

const dateText = (d: Date | null) =>
    d ? new Intl.DateTimeFormat('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' }).format(d) : '';

/** "2025-10-22" as an Excel date: midnight UTC, so no time zone moves it to the day before */
const isoDate = (v: unknown): Date | null => {
    const m = typeof v === 'string' ? /^(\d{4})-(\d{2})-(\d{2})/.exec(v) : null;
    return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])) : null;
};

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

const moneyFormat = (currency: string): string => {
    let digits = 2;
    try {
        digits = new Intl.NumberFormat('en-NZ', { style: 'currency', currency }).resolvedOptions()
            .maximumFractionDigits as number;
    } catch {
        // An unknown code keeps two decimals
    }
    return digits > 0 ? `#,##0.${'0'.repeat(digits)}` : '#,##0';
};

const NUMBER_FORMAT: Record<Exclude<Kind, 'money'>, string> = {
    text: '@',
    int: '#,##0',
    estimate: '#,##0',
    decimal: '#,##0.0',
    // Response rates run to hundredths of a percent; shares are read to a tenth
    rate: '0.00%',
    share: '0.0%',
    date: 'd mmm yyyy',
};

/** The sheets, in the dashboard's order and vocabulary ("Visits", not "Responses") */
export const buildSheets = (data: ReportData): SheetSpec[] => {
    const s = data.impressionSummary?.summary_data ?? {};
    const currency: string = s.currency_code || data.responsesBySite?.table_data?.[0]?.currency_code || 'NZD';
    const money = (label: string): Column => ({ label: `${label} (${currency})`, kind: 'money' });
    const ibv = data.impressionsBeforeVisit?.summary_data ?? {};
    const frequency = num(data.reachBreakdown?.total_frequency);
    const sheets: SheetSpec[] = [];

    const summaryRows: Array<[string, Kind, number | null]> = [
        [`Ad spend (${currency})`, 'money', num(s.Ad_Spend)],
        ['Impressions', 'int', num(s.Actual_Impressions)],
        ['Visits', 'int', num(s.Total_Responses)],
        ['Response rate (%)', 'rate', num(s.Response_Rate)],
        [`CPM (${currency})`, 'money', num(s.CPM_Actual)],
        [`Cost per visit (${currency})`, 'money', num(s.Current_CPR)],
        ['Unique reach (households, estimate)', 'estimate', num(data.uniqueReach?.unique_viewers)],
        ['Average frequency', 'decimal', frequency],
        ['Average impressions before a visit', 'decimal', num(ibv.Total_Views_Occurrence)],
        ['Impressions by which 80% of visits had come', 'int', num(ibv.Views_for_80_Percent)],
    ];
    if (summaryRows.some(([, , v]) => v !== null)) {
        sheets.push({
            name: 'Summary',
            title: 'Summary',
            about: 'The campaign totals for the selected filters, as in the strip across the top of the dashboard.',
            columns: [
                { label: 'Measure', kind: 'text', width: 46 },
                { label: 'Value', kind: 'text', width: 18 },
            ],
            rows: summaryRows.filter(([, , v]) => v !== null).map(([label, , v]) => [label, v]),
            rowKinds: summaryRows.filter(([, , v]) => v !== null).map(([, k]) => k),
        });
    }

    const overTime: Any[] = data.responsesOverTime?.table_data ?? [];
    if (overTime.length)
        sheets.push({
            name: 'Visits over time',
            title: 'Impressions and visits by day',
            about: 'The most recent seven days are still filling in: visits keep arriving for a week after an impression.',
            columns: [
                { label: 'Date', kind: 'date' },
                { label: 'Impressions', kind: 'int' },
                { label: 'Visits', kind: 'int' },
                { label: 'Same-day visits', kind: 'int' },
            ],
            rows: overTime.map((r) => [
                isoDate(r.Date),
                num(r.Total_Impressions),
                num(r.Total_Responses),
                num(r.Same_Day),
            ]),
        });

    const timing = responseTimingBins(data.visitResponseTiming);
    if (timing.some((b) => b.visits > 0))
        sheets.push({
            name: 'Visit timing',
            title: 'How soon visits followed an impression',
            about: 'Visits by the time between the impression and the visit. The bins widen with time, so compare visits an hour, not the totals.',
            columns: [
                { label: 'Time from impression to visit', kind: 'text', width: 30 },
                { label: 'Visits an hour', kind: 'decimal' },
                { label: 'Visits', kind: 'int' },
                { label: 'Share of visits (%)', kind: 'share' },
            ],
            rows: timing.map((b) => [b.label, b.perHour, b.visits, b.share / 100]),
        });

    const ibvRows: Any[] = data.impressionsBeforeVisit?.table_data ?? [];
    if (ibvRows.length)
        sheets.push({
            name: 'Impressions before visit',
            title: 'Impressions seen before a visit',
            about: 'Visits by the number of impressions the household had seen beforehand.',
            columns: [
                { label: 'Impressions seen before the visit', kind: 'int', width: 32 },
                { label: 'Visits', kind: 'int' },
            ],
            rows: [...ibvRows]
                .sort((a, b) => (num(a.Views) ?? 0) - (num(b.Views) ?? 0))
                .map((r) => [num(r.Views), num(r.Impressions_Before_Visit)]),
        });

    const regions: Any[] = data.responsesByRegion?.table_data ?? [];
    if (regions.length)
        sheets.push({
            name: 'Visits by region',
            title: 'Impressions and visits by region',
            about: 'Share of visits is the region’s share of all visits in this report.',
            columns: [
                { label: 'Region', kind: 'text', width: 26 },
                { label: 'Impressions', kind: 'int' },
                { label: 'Visits', kind: 'int' },
                { label: 'Share of visits (%)', kind: 'share' },
            ],
            rows: catchAllLast(regions).map((r) => [
                regionName(r),
                num(r.Actual_Impressions),
                num(r.Responses),
                num(r.Percentage),
            ]),
        });

    const reachRegions: Any[] = data.uniqueReach?.by_region ?? [];
    if (reachRegions.length)
        sheets.push({
            name: 'Reach by region',
            title: 'Unique reach by region',
            about: 'Households reached, estimated (to three significant figures); a household reached in two regions counts in both, so the regions do not add up to the total.',
            columns: [
                { label: 'Region', kind: 'text', width: 26 },
                { label: 'Country', kind: 'text' },
                { label: 'Unique reach (households, estimate)', kind: 'estimate', width: 22 },
            ],
            rows: catchAllLast(reachRegions).map((r) => [
                regionName({ Region: r.region_name, is_country_other: r.is_country_other }),
                r.country_code ?? '',
                num(r.unique_viewers),
            ]),
        });

    const sites: Any[] = data.responsesBySite?.table_data ?? [];
    if (sites.length)
        sheets.push({
            name: 'Visits by page',
            title: 'Visits by page (site tag)',
            about: 'Each row is a site tag: a page or action on the client’s website.',
            columns: [
                { label: 'Page', kind: 'text', width: 40 },
                { label: 'Visits', kind: 'int' },
                { label: 'Response rate (%)', kind: 'rate' },
                money('Cost per visit'),
            ],
            rows: sites.map((r) => [r.Site ?? '', num(r.Responses), num(r.Response_Rate_Percent), num(r.CPR)]),
        });

    // The three cuts of Performance Details, one sheet each, with its columns. Shares are each row's
    // part of its own column's total, as the dashboard shows them.
    const performanceSheet = (
        name: string,
        about: string,
        rows: Any[],
        nameColumns: Array<{ label: string; width?: number; value: (r: Any) => string }>
    ) => {
        if (!rows.length) return;
        const total = (key: string) => rows.reduce((n, r) => n + (num(r[key]) ?? 0), 0);
        const share = (r: Any, key: string, all: number) => (all > 0 ? (num(r[key]) ?? 0) / all : null);
        const impressions = total('Total_Impressions');
        const visits = total('Total_Responses');
        sheets.push({
            name,
            title: `${name}: spend and visits`,
            about,
            columns: [
                ...nameColumns.map((c) => ({ label: c.label, kind: 'text' as const, width: c.width ?? 30 })),
                { label: 'Impressions', kind: 'int' },
                { label: 'Share of impressions (%)', kind: 'share' },
                money('Spend'),
                { label: 'Visits', kind: 'int' },
                { label: 'Share of visits (%)', kind: 'share' },
                { label: 'Response rate (%)', kind: 'rate' },
                money('Cost per visit'),
            ],
            rows: [...rows]
                .sort((a, b) => (num(b.Total_Impressions) ?? 0) - (num(a.Total_Impressions) ?? 0))
                .map((r) => [
                    ...nameColumns.map((c) => c.value(r)),
                    num(r.Total_Impressions),
                    share(r, 'Total_Impressions', impressions),
                    num(r.Ad_Spend),
                    num(r.Total_Responses),
                    share(r, 'Total_Responses', visits),
                    num(r.Response_Rate_Percent),
                    num(r.Current_CPR),
                ]),
        });
    };
    performanceSheet('Creatives', 'One row per creative.', data.creativePerformanceCost?.table_data ?? [], [
        { label: 'Creative', width: 36, value: (r) => r.Creative ?? '' },
        { label: 'Length or size', width: 14, value: (r) => r.Duration ?? '' },
    ]);
    performanceSheet('Channels', 'One row per channel.', data.channelPerformanceCost?.table_data ?? [], [
        { label: 'Channel', value: (r) => r.Channel ?? '' },
    ]);
    performanceSheet('Bookings', 'One row per booking.', data.bookingPerformanceCost?.table_data ?? [], [
        { label: 'Booking', width: 36, value: (r) => r.Booking ?? '' },
        { label: 'Channel', width: 22, value: (r) => r.Channel ?? '' },
    ]);

    const heat = (key: string, name: string, title: string, kind: Kind, about: string) => {
        const rows: Any[] = data.impressionHeatmap?.[key] ?? [];
        if (!rows.length) return;
        sheets.push({
            name,
            title,
            about,
            columns: [
                { label: 'Daypart', kind: 'text', width: 14 },
                ...DAYS.map((d) => ({ label: d, kind })),
                { label: 'Total', kind },
            ],
            rows: rows.map((r) => [r.time ?? '', ...DAYS.map((d) => num(r[d])), num(r.Total)]),
            colourScale: true,
        });
    };
    heat('impressions', 'Impressions by daypart', 'Impressions by daypart and weekday', 'int', 'Darker is more.');
    heat('webhits', 'Visits by daypart', 'Visits by daypart and weekday', 'int', 'Darker is more.');
    heat(
        'response_ratio',
        'Response rate by daypart',
        'Response rate by daypart and weekday (%)',
        'rate',
        'Visits per impression; darker is higher.'
    );

    return sheets;
};

/** The workbook: a cover sheet, then one sheet per table */
export const buildWorkbook = (ExcelJSLib: typeof import('exceljs'), data: ReportData, ctx: ReportContext) => {
    const wb = new ExcelJSLib.Workbook();
    wb.creator = 'Infinitum by Mime Analytics';
    wb.created = ctx.exportedAt;
    wb.modified = ctx.exportedAt;

    const sheets = buildSheets(data);
    const s = data.impressionSummary?.summary_data ?? {};
    const currency: string = s.currency_code || 'NZD';
    const startDate = asLocalDate(ctx.startDate);
    const endDate = asLocalDate(ctx.endDate);
    const range = startDate && endDate ? `${dateText(startDate)} to ${dateText(endDate)}` : 'All dates';
    const subtitle = `${ctx.client} · ${range}`;

    const font = (extra: Partial<ExcelJS.Font> = {}): Partial<ExcelJS.Font> => ({ name: FONT, size: 11, ...extra });
    const title = (ws: ExcelJS.Worksheet, text: string, sub?: string) => {
        ws.getCell('A1').value = text;
        ws.getCell('A1').font = font({ size: 16, bold: true });
        if (sub) {
            ws.getCell('A2').value = sub;
            ws.getCell('A2').font = font({ color: { argb: 'FF555555' } });
        }
    };

    // ── Cover ────────────────────────────────────────────────────────────
    // Printed, every sheet fits one page wide (as many pages tall as it needs)
    const printable = {
        pageSetup: { fitToPage: true, fitToWidth: 1, fitToHeight: 0, orientation: 'landscape' as const },
    };
    const cover = wb.addWorksheet('Cover', printable);
    title(cover, `Infinitum campaign report: ${ctx.client}`, 'Infinitum by Mime Analytics');
    const about: Array<[string, string]> = [
        ['Client', ctx.client],
        ['Dates', range],
        [
            'Filters',
            ctx.filters.length
                ? ctx.filters
                      .map(
                          (f) =>
                              `${f.label}: ${f.values.slice(0, 8).join(', ')}${f.values.length > 8 ? ` and ${f.values.length - 8} more` : ''}`
                      )
                      .join('; ')
                : 'None: every campaign, booking, channel, region and page',
        ],
        ['Currency', currency],
        [
            'Exported',
            new Intl.DateTimeFormat('en-NZ', {
                // dateStyle cannot be combined with timeZoneName; spell the fields out
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                timeZoneName: 'short',
            }).format(ctx.exportedAt),
        ],
        ['Source', 'Infinitum dashboard (infinitum8.com), Mime Analytics'],
        ['Reach', 'Unique reach figures are estimates, to three significant figures.'],
        ['Recent days', 'Visits keep arriving for a week after an impression: the latest seven days will still rise.'],
    ];
    about.forEach(([k, v], i) => {
        const row = cover.getRow(4 + i);
        row.getCell(1).value = k;
        row.getCell(1).font = font({ bold: true });
        row.getCell(2).value = v;
        row.getCell(2).font = font();
        row.getCell(2).alignment = { wrapText: true, vertical: 'top' };
    });
    cover.getColumn(1).width = 16;
    cover.getColumn(2).width = 90;
    if (ctx.logo) {
        // Cover only, small, in its own column right of the text so no title runs under it. exceljs
        // cannot give an image alt text, so the logo is
        // decoration: the name "Infinitum by Mime Analytics" is written in A2 for screen readers
        const id = wb.addImage({ base64: ctx.logo.dataUrl.split(',')[1], extension: 'png' });
        const h = 36;
        cover.addImage(id, {
            tl: { col: 2.15, row: 0.15 },
            ext: { width: (ctx.logo.width / ctx.logo.height) * h, height: h },
        });
    }
    const tocTop = 4 + about.length + 1;
    cover.getCell(`A${tocTop}`).value = 'Contents';
    cover.getCell(`A${tocTop}`).font = font({ size: 13, bold: true });
    cover.addTable({
        name: 'Contents',
        ref: `A${tocTop + 1}`,
        headerRow: true,
        style: { theme: 'TableStyleLight1', showRowStripes: false },
        columns: [{ name: 'Sheet' }, { name: 'What it shows' }],
        rows: sheets.map((sh) => [sh.name, sh.title]),
    });
    sheets.forEach((sh, i) => {
        const cell = cover.getCell(`A${tocTop + 2 + i}`);
        cell.value = { text: sh.name, hyperlink: `#'${sh.name}'!A1` };
        cell.font = font({ color: { argb: 'FF' + colors.excelHeader }, underline: true });
        cover.getCell(`B${tocTop + 2 + i}`).font = font();
    });
    styleHeader(cover.getRow(tocTop + 1), 2, font);

    // ── One sheet per table ─────────────────────────────────────────────
    sheets.forEach((sh, si) => {
        const ws = wb.addWorksheet(sh.name, printable);
        title(ws, sh.title, subtitle);
        ws.getCell('A3').value = sh.about;
        ws.getCell('A3').font = font({ italic: false, color: { argb: 'FF555555' } });
        const top = 5;
        ws.addTable({
            name: `T${si + 1}_${sh.name.replace(/[^A-Za-z0-9]/g, '')}`,
            ref: `A${top}`,
            headerRow: true,
            style: { theme: 'TableStyleLight1', showRowStripes: false },
            columns: sh.columns.map((c) => ({ name: c.label })),
            rows: sh.rows.map((r) =>
                r.map((v, ci) =>
                    sh.columns[ci].kind === 'estimate' && typeof v === 'number' ? estimate(v) : (v ?? null)
                )
            ),
        });
        styleHeader(
            ws.getRow(top),
            sh.columns.length,
            font,
            sh.columns.map((c, ci) => c.kind !== 'text' || (sh.rowKinds !== undefined && ci === 1))
        );

        const { rowKinds } = sh;
        sh.columns.forEach((c, ci) => {
            const col = ws.getColumn(ci + 1);
            const longest = Math.max(c.label.length, ...sh.rows.map((r) => String(r[ci] ?? '').length));
            col.width = c.width ?? Math.min(Math.max(longest + 2, 12), 40);
            for (let ri = 0; ri < sh.rows.length; ri++) {
                const cell = ws.getCell(top + 1 + ri, ci + 1);
                cell.font = font();
                const kind = rowKinds && ci === 1 ? rowKinds[ri] : c.kind;
                if (kind === 'estimate' && typeof cell.value === 'number') cell.value = estimate(cell.value);
                if (kind === 'text') continue;
                cell.numFmt = kind === 'money' ? moneyFormat(currency) : NUMBER_FORMAT[kind];
                cell.alignment = { horizontal: 'right' };
            }
        });

        if (sh.colourScale && sh.rows.length) {
            // Excel's own scale, one hue light to dark, as the dashboard's heatmaps: live if a figure is
            // edited, and a single hue reads for colour-blind readers. Totals are left out of the scale.
            const dataRows = sh.rows.filter((r) => String(r[0]).toLowerCase() !== 'total').length;
            ws.addConditionalFormatting({
                ref: `B${top + 1}:H${top + dataRows}`,
                rules: [
                    {
                        type: 'colorScale',
                        priority: 1,
                        cfvo: [{ type: 'min' }, { type: 'max' }],
                        color: [{ argb: 'FFFFFFFF' }, { argb: 'FF8FA4D6' }],
                    },
                ],
            });
        }
    });

    return wb;
};

/** Header row: Infinitum Blue, white bold text, wrapped so units sit under the label */
function styleHeader(
    row: ExcelJS.Row,
    count: number,
    font: (extra?: Partial<ExcelJS.Font>) => Partial<ExcelJS.Font>,
    numeric: boolean[] = []
) {
    for (let c = 1; c <= count; c++) {
        const cell = row.getCell(c);
        cell.font = font({ bold: true, color: { argb: 'FFFFFFFF' } });
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + colors.excelHeader } };
        // A number's header sits over its numbers: right-aligned, as on the dashboard
        cell.alignment = { wrapText: true, vertical: 'middle', horizontal: numeric[c - 1] ? 'right' : 'left' };
    }
    row.height = 32;
}
